import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import {
    MapBaseSource,
    MapLayerSource,
    MapSourceTypeEnum,
} from '@connect-services/client/components/map/map.component';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    GroupBucket,
    SelectComponent,
    SelectOption,
} from '@connect-services/src/lib/components/select/select.component';
import { SelectByGroupComponent } from '@connect-services/src/lib/components/select-by-group/select-by-group.component';
import { Layer } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, SelectByGroupComponent, SelectComponent],
    selector: 'app-map-modal-snap',
    templateUrl: './map-modal-snap.component.html',
})
export class MapModalSnapComponent implements OnInit {
    protected readonly defaultDistanceOption = signal<SelectOption<number>[]>([]);
    protected readonly defaultSourceOptions = signal<SelectOption<string[]>[]>([]);
    protected readonly distanceFormControl = new FormControl<number>({
        disabled: false,
        value: 20,
    });
    protected readonly fieldInterventionOptions = signal<SelectOption<string[]>[]>([]);
    protected readonly hasFieldInterventionPermission = computed<boolean>(
        () =>
            this.fieldInterventionPermissionService.canReadFieldIntervention() ||
            this.fieldInterventionPermissionService.canManageFieldIntervention(),
    );
    protected readonly layerDistances = signal<SelectOption<number>[]>([
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '50', value: 50 },
        { label: '100', value: 100 },
        { label: '200', value: 200 },
    ]);
    protected readonly layerOptions = signal<SelectOption<string[]>[]>([]);
    protected readonly mapEditGeomService = inject(MapEditGeomService);
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly optionsByGroup = computed<GroupBucket<string[]>>(() => {
        const hasFieldInterventionPermission = this.hasFieldInterventionPermission();

        return this.createGroupBucket(
            this.fieldInterventionOptions(),
            this.layerOptions(),
            hasFieldInterventionPermission,
        );
    });
    protected readonly selectedDistanceOptions = signal<SelectOption<number>[]>([]);
    protected readonly selectedOptions = signal<Set<string>>(new Set());

    private readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);
    private readonly sourcesService = inject(SourcesService);
    private translocoService = inject(TranslocoService);

    public ngOnInit(): void {
        this.setSelectOptions();
    }

    public selectDistancesOptions(distances: number[]): void {
        if (!distances[0]) {
            this.selectedDistanceOptions.set([]);

            return;
        }

        if (!isFinite(distances[0])) {
            return;
        }

        this.selectedDistanceOptions.set([{ label: String(distances[0]), value: distances[0] }]);
        this.mapEditGeomService.snapTolerance.set(Number(distances[0]));
    }

    public selectLayerOptions(layersIds: string[][]): void {
        const uniqueLayerIds = new Set(layersIds.flat());

        if (!uniqueLayerIds.size) {
            this.selectedDistanceOptions.set([]);
            this.mapEditModeService.selectedSnapSources.set([]);
            this.mapEditGeomService.selectedSnapPoint.set(null);

            return;
        }

        if (!this.selectedDistanceOptions().length) {
            this.selectedDistanceOptions.set([{ label: '20', value: 20 }]);
            this.mapEditGeomService.snapTolerance.set(20);
        }

        this.mapEditModeService.selectedSnapSources.set([...uniqueLayerIds]);
    }

    private createGroupBucket(
        fieldInterventionOptions: SelectOption<string[]>[],
        layerOptions: SelectOption<string[]>[],
        hasFieldInterventionPermission: boolean,
    ): GroupBucket<string[]> {
        return {
            ...(hasFieldInterventionPermission
                ? { [this.translocoService.translate('fieldIntervention')]: fieldInterventionOptions }
                : undefined),
            [this.translocoService.translate('layer')]: layerOptions,
        };
    }

    private groupSourcesByType(sources: MapBaseSource<unknown>[]): {
        fieldIntervention: Record<string, string[]>;
        layer: Record<string, string[]>;
    } {
        return sources.reduce(
            (
                acc: { fieldIntervention: Record<string, string[]>; layer: Record<string, string[]> },
                source: MapBaseSource<unknown>,
            ) => {
                switch (source.type) {
                    case MapSourceTypeEnum.FIELD_INTERVENTION: {
                        acc.fieldIntervention[source.name] = [
                            ...(acc.fieldIntervention[source.name] ?? []),
                            source.sourceId,
                        ];

                        break;
                    }

                    case MapSourceTypeEnum.LAYER: {
                        if (
                            source instanceof MapLayerSource &&
                            (source.isInterstandard ||
                                ![Layer.TypeEnum.TABLE, Layer.TypeEnum.INTERSTANDARD].includes(source.data.type))
                        ) {
                            break;
                        }

                        acc.layer[source.name] = [...(acc.layer[source.name] ?? []), source.sourceId];

                        break;
                    }

                    default:
                        break;
                }

                return acc;
            },
            { fieldIntervention: {}, layer: {} },
        );
    }

    private isInSelectedSnapSources(ids: string[]): boolean {
        return ids.some((id: string) => this.mapEditModeService.selectedSnapSources().includes(id));
    }

    private setSelectOptions(): void {
        const groupedSources = this.groupSourcesByType(this.sourcesService.sources());

        this.layerOptions.set(
            Object.keys(groupedSources.layer).map((layerName: string): SelectOption<string[]> => {
                return { label: layerName, value: groupedSources.layer[layerName] };
            }),
        );

        this.fieldInterventionOptions.set(
            Object.keys(groupedSources.fieldIntervention).map((layerName: string): SelectOption<string[]> => {
                return { label: layerName, value: groupedSources.fieldIntervention[layerName] };
            }),
        );

        this.defaultSourceOptions.set([
            ...this.layerOptions().filter((option: SelectOption<string[]>) =>
                this.isInSelectedSnapSources(option.value),
            ),
            ...this.fieldInterventionOptions().filter((option: SelectOption<string[]>) =>
                this.isInSelectedSnapSources(option.value),
            ),
        ]);

        if (!this.defaultSourceOptions().length) {
            return;
        }

        this.defaultDistanceOption.set(
            this.layerDistances().filter(
                (option: SelectOption<number>) => option.value === this.mapEditGeomService.snapTolerance(),
            ),
        );
    }
}
